import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, Building2, AlertCircle, Users, Scale, Euro, Bell, ExternalLink, Loader2, Trash2, Edit3, Filter, TrendingUp } from "lucide-react";
import { hrListClients, hrPortfolioSummary, hrDeleteClient, formatApiError } from "../lib/api";
import { HRClientDialog } from "../components/HRForms";
import HRLayout from "../components/HRLayout";

const fmtEuro = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v || 0);

const riskStyles = {
    verde: { chip: "bg-emerald-100 text-emerald-700 border-emerald-200", dot: "bg-emerald-500", label: "Basso" },
    giallo: { chip: "bg-amber-100 text-amber-800 border-amber-200", dot: "bg-amber-500", label: "Medio" },
    rosso: { chip: "bg-red-100 text-red-700 border-red-200", dot: "bg-red-500", label: "Alto" },
};

export default function HRPortfolio() {
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);
    const [summary, setSummary] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [filterRisk, setFilterRisk] = useState("all");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editingClient, setEditingClient] = useState(null);

    const reload = async () => {
        setLoading(true);
        setError(null);
        try {
            const [cs, s] = await Promise.all([hrListClients(), hrPortfolioSummary()]);
            setClients(cs);
            setSummary(s);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { reload(); }, []);

    const handleDelete = async (client) => {
        if (!window.confirm(`Eliminare "${client.ragione_sociale}" e tutti i dati associati?`)) return;
        try { await hrDeleteClient(client.id); await reload(); } catch (err) { alert(formatApiError(err)); }
    };

    const filtered = useMemo(() => {
        const q = search.toLowerCase();
        return clients.filter((c) => {
            if (filterRisk !== "all" && c.livello_rischio !== filterRisk) return false;
            if (q && !c.ragione_sociale.toLowerCase().includes(q) && !(c.settore || "").toLowerCase().includes(q)) return false;
            return true;
        });
    }, [clients, search, filterRisk]);

    return (
        <HRLayout
            title="Portfolio Clienti"
            subtitle="Monitora compliance giuslavoristica, contenziosi e fondo rischi legali di tutte le aziende clienti."
            breadcrumbs={[{ label: "Portfolio" }]}
            actions={
                <button onClick={() => { setEditingClient(null); setDialogOpen(true); }} className="hr-btn-primary" data-testid="btn-new-client">
                    <Plus size={14} /> Nuovo Cliente
                </button>
            }
        >
            {/* KPI Cards */}
            {summary && (
                <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="hr-portfolio-kpis">
                    <KPICard icon={Building2} label="Clienti in portafoglio" value={summary.total_clients} />
                    <KPICard icon={Users} label="Dipendenti totali" value={summary.total_employees} />
                    <KPICard icon={Scale} label="Petitum totale" value={fmtEuro(summary.total_petitum)} mono />
                    <KPICard icon={Euro} label="Fondo rischi consigliato" value={fmtEuro(summary.total_fondo_rischi)} mono accent />
                </div>
            )}

            {/* Risk Distribution */}
            {summary && summary.total_clients > 0 && (
                <div className="hr-card shadow-xs p-5" data-testid="hr-portfolio-risk-dist">
                    <div className="flex items-center justify-between mb-3">
                        <div className="text-[11px] uppercase tracking-wider font-bold text-slate-500 flex items-center gap-2">
                            <TrendingUp size={13} /> Distribuzione Rischio Portafoglio
                        </div>
                        <span className="text-[11px] text-slate-400">{summary.total_clients} clienti totali</span>
                    </div>
                    <div className="flex items-center gap-6 flex-wrap">
                        <RiskChip count={summary.distribuzione_rischio.verde} total={summary.total_clients} level="verde" />
                        <RiskChip count={summary.distribuzione_rischio.giallo} total={summary.total_clients} level="giallo" />
                        <RiskChip count={summary.distribuzione_rischio.rosso} total={summary.total_clients} level="rosso" />
                    </div>
                    {/* Progress bar */}
                    <div className="mt-4 h-2 w-full flex rounded-full overflow-hidden bg-slate-100">
                        {["verde", "giallo", "rosso"].map((lv) => {
                            const pct = summary.total_clients ? (summary.distribuzione_rischio[lv] / summary.total_clients) * 100 : 0;
                            if (!pct) return null;
                            return <div key={lv} className={riskStyles[lv].dot} style={{ width: `${pct}%` }} />;
                        })}
                    </div>
                </div>
            )}

            {/* Filters */}
            <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
                <div className="relative flex-1">
                    <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                    <input
                        type="text"
                        placeholder="Cerca per nome azienda o settore..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="hr-input pl-9"
                        data-testid="input-search-clients"
                    />
                </div>
                <div className="flex items-center gap-1 bg-white border border-slate-200 rounded-lg p-1 shadow-xs">
                    <Filter size={13} className="text-slate-400 ml-2 mr-1" />
                    {[
                        { k: "all", label: "Tutti" },
                        { k: "rosso", label: "Alto", dot: "bg-red-500" },
                        { k: "giallo", label: "Medio", dot: "bg-amber-500" },
                        { k: "verde", label: "Basso", dot: "bg-emerald-500" },
                    ].map((f) => (
                        <button
                            key={f.k}
                            onClick={() => setFilterRisk(f.k)}
                            data-testid={`filter-${f.k}`}
                            className={`inline-flex items-center gap-1.5 px-3 h-7 rounded-md text-[11px] font-semibold transition-colors ${
                                filterRisk === f.k ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-100"
                            }`}
                        >
                            {f.dot && <span className={`w-1.5 h-1.5 rounded-full ${f.dot}`} />}
                            {f.label}
                        </button>
                    ))}
                </div>
            </div>

            {/* Loading */}
            {loading && (
                <div className="hr-card p-16 text-center">
                    <Loader2 size={20} className="animate-spin mx-auto text-slate-400 mb-2" />
                    <div className="text-xs text-slate-500">Caricamento portfolio...</div>
                </div>
            )}
            {error && (
                <div className="p-4 bg-red-50 border border-red-200 text-red-700 rounded-xl text-sm flex items-start gap-2" data-testid="hr-error">
                    <AlertCircle size={16} className="mt-0.5 flex-shrink-0" /> {error}
                </div>
            )}

            {/* Clients Table */}
            {!loading && !error && (
                <div className="hr-card shadow-xs overflow-hidden" data-testid="hr-clients-table">
                    {filtered.length === 0 ? (
                        <div className="p-16 text-center">
                            <div className="w-14 h-14 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-3">
                                <Building2 size={24} className="text-slate-400" />
                            </div>
                            <div className="text-sm font-semibold text-slate-900 mb-1">
                                {clients.length === 0 ? "Nessun cliente in portafoglio" : "Nessun risultato"}
                            </div>
                            <div className="text-xs text-slate-500 mb-5">
                                {clients.length === 0 ? "Inizia aggiungendo la tua prima azienda cliente" : "Prova a modificare i filtri"}
                            </div>
                            {clients.length === 0 && (
                                <button onClick={() => { setEditingClient(null); setDialogOpen(true); }} className="hr-btn-primary mx-auto">
                                    <Plus size={14} /> Aggiungi primo cliente
                                </button>
                            )}
                        </div>
                    ) : (
                        <div className="overflow-x-auto">
                            <table className="w-full min-w-[1000px]">
                                <thead>
                                    <tr className="bg-slate-50/80 border-b border-slate-200">
                                        <Th>Azienda</Th>
                                        <Th>CCNL</Th>
                                        <Th center>Dipendenti</Th>
                                        <Th center>Health Score</Th>
                                        <Th center>Cause</Th>
                                        <Th right>Esposizione</Th>
                                        <Th center>Scad. 30gg</Th>
                                        <Th right>Azioni</Th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map((c) => {
                                        const rs = riskStyles[c.livello_rischio] || riskStyles.verde;
                                        return (
                                            <tr key={c.id} className="border-b border-slate-100 hover:bg-slate-50/60" data-testid={`hr-client-row-${c.id}`}>
                                                <td className="px-4 py-3.5">
                                                    <button onClick={() => navigate(`/admin/hr/clients/${c.id}`)} data-testid={`btn-open-client-${c.id}`} className="text-left group">
                                                        <div className="text-sm font-semibold text-slate-900 group-hover:text-blue-600 transition-colors">{c.ragione_sociale}</div>
                                                        {c.settore && <div className="text-[11px] text-slate-500 mt-0.5">{c.settore}</div>}
                                                    </button>
                                                </td>
                                                <td className="px-4 py-3.5 text-xs text-slate-600 max-w-[200px]">
                                                    <div className="truncate">{c.ccnl_nome || "—"}</div>
                                                </td>
                                                <td className="px-4 py-3.5 text-center font-mono text-xs text-slate-700 font-semibold">{c.totale_dipendenti}</td>
                                                <td className="px-4 py-3.5">
                                                    <div className="flex items-center justify-center gap-2">
                                                        <span className="font-mono text-base font-bold text-slate-900">{c.health_score}</span>
                                                        <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full border text-[9px] uppercase tracking-wider font-bold ${rs.chip}`}>
                                                            <span className={`w-1.5 h-1.5 rounded-full ${rs.dot}`} />
                                                            {rs.label}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td className="px-4 py-3.5 text-center font-mono text-xs text-slate-700">{c.cause_attive}</td>
                                                <td className="px-4 py-3.5 text-right">
                                                    <div className="text-xs font-mono text-slate-900 font-semibold">{fmtEuro(c.petitum_totale)}</div>
                                                    <div className="text-[9px] text-slate-400 uppercase tracking-wider mt-0.5">Petitum</div>
                                                </td>
                                                <td className="px-4 py-3.5 text-center">
                                                    {c.scadenze_30gg > 0 ? (
                                                        <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-amber-100 text-amber-800 border border-amber-200 rounded-full text-[10px] font-bold" data-testid={`badge-scad-${c.id}`}>
                                                            <Bell size={10} /> {c.scadenze_30gg}
                                                        </span>
                                                    ) : <span className="text-slate-300 text-xs">—</span>}
                                                </td>
                                                <td className="px-4 py-3.5">
                                                    <div className="flex items-center gap-1 justify-end">
                                                        <button onClick={() => navigate(`/admin/hr/clients/${c.id}`)} data-testid={`btn-detail-${c.id}`} className="p-1.5 hover:bg-slate-100 rounded-md text-slate-500 hover:text-slate-900 transition-colors" title="Apri dashboard">
                                                            <ExternalLink size={13} />
                                                        </button>
                                                        <button onClick={() => { setEditingClient(c); setDialogOpen(true); }} data-testid={`btn-edit-${c.id}`} className="p-1.5 hover:bg-slate-100 rounded-md text-slate-500 hover:text-slate-900 transition-colors" title="Modifica">
                                                            <Edit3 size={13} />
                                                        </button>
                                                        <button onClick={() => handleDelete(c)} data-testid={`btn-delete-${c.id}`} className="p-1.5 hover:bg-red-50 rounded-md text-slate-400 hover:text-red-600 transition-colors" title="Elimina">
                                                            <Trash2 size={13} />
                                                        </button>
                                                    </div>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </div>
            )}

            <HRClientDialog open={dialogOpen} onOpenChange={setDialogOpen} client={editingClient} onSaved={reload} />
        </HRLayout>
    );
}

function Th({ children, center, right }) {
    const align = right ? "text-right" : center ? "text-center" : "text-left";
    return <th className={`px-4 py-3 text-[11px] uppercase tracking-wider font-semibold text-slate-500 ${align}`}>{children}</th>;
}

function KPICard({ icon: Icon, label, value, mono, accent }) {
    return (
        <div className={`hr-card hr-card-hover shadow-xs p-5 ${accent ? "bg-gradient-to-br from-slate-900 to-slate-800 text-white border-slate-900" : ""}`}>
            <div className="flex items-center justify-between mb-3">
                <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${accent ? "bg-white/10" : "bg-slate-100"}`}>
                    <Icon size={16} className={accent ? "text-amber-300" : "text-slate-600"} />
                </div>
                <div className={`text-[10px] uppercase tracking-wider font-semibold ${accent ? "text-slate-400" : "text-slate-400"}`}>{label}</div>
            </div>
            <div className={`text-2xl font-extrabold tracking-tight ${mono ? "font-mono" : ""} ${accent ? "text-white" : "text-slate-900"}`}>
                {value}
            </div>
        </div>
    );
}

function RiskChip({ count, total, level }) {
    const rs = riskStyles[level];
    const pct = total > 0 ? Math.round((count / total) * 100) : 0;
    return (
        <div className="flex items-center gap-2.5">
            <span className={`w-2.5 h-2.5 rounded-full ${rs.dot} ring-4 ring-opacity-20`} style={{ "--tw-ring-color": "currentColor" }} />
            <div>
                <div className="text-xs font-semibold text-slate-900">Rischio {rs.label}</div>
                <div className="text-[11px] text-slate-500 font-mono">{count} client{count === 1 ? "e" : "i"} · {pct}%</div>
            </div>
        </div>
    );
}
